import { Flex } from '@sequencehq/core-components'
import { ConfigurationManagementProps } from 'modules/Integrations/domain'
import invariant from 'tiny-invariant'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { AnrokIntegrationConfig } from 'Integrations/integrationsConfig/anrok/anrok.integration.config'
import AnrokAuthenticationCard from './cards/AnrokAuthenticationCard'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useQuery } from '@sequencehq/api/utils'

export const AnrokConfigurationManagement = ({
  updateValidationState,
  updateConfiguration,
  integration
}: ConfigurationManagementProps) => {
  invariant(integration.id === 'Anrok', 'Integration is not Anrok')

  const credentialsQuery = useQuery(
    dashboardv99990101Client.getStoredAnrokCredentials
  )

  const AnrokIntegration = useMemo(() => {
    if (credentialsQuery.data) {
      return {
        obfuscatedApiKey: credentialsQuery.data.obfuscatedApiKey
      } as AnrokIntegrationConfig
    }

    return (
      (integration.configuration as AnrokIntegrationConfig) || {
        obfuscatedApiKey: ''
      }
    )
  }, [credentialsQuery.data, integration.configuration])

  const [wasConnectionSuccessful, setWasConnectionSuccessful] = useState(false)
  const [pendingCredentials, setPendingCredentials] = useState<{
    apiKey: string
  } | null>(null)

  const processCredentials = useCallback(
    (credentials: { apiKey: string }) => {
      const newConfiguration: AnrokIntegrationConfig = {
        apiKey: credentials.apiKey,
        obfuscatedApiKey: AnrokIntegration.obfuscatedApiKey || ''
      }
      updateConfiguration(newConfiguration)
      updateValidationState(true)
    },
    [
      updateConfiguration,
      updateValidationState,
      AnrokIntegration.obfuscatedApiKey
    ]
  )

  useEffect(() => {
    if (pendingCredentials) {
      processCredentials(pendingCredentials)
      setPendingCredentials(null)
    }
  }, [pendingCredentials, processCredentials])

  const updateCredentials = useCallback((credentials: { apiKey: string }) => {
    setWasConnectionSuccessful(true)
    setPendingCredentials(credentials)
  }, [])

  const resetValidationState = useCallback(() => {
    if (wasConnectionSuccessful) {
      updateValidationState(false)
      setWasConnectionSuccessful(false)
    }
  }, [wasConnectionSuccessful, updateValidationState])

  const existingCredentials = useMemo(
    () => ({
      apiKey: AnrokIntegration.obfuscatedApiKey
    }),
    [AnrokIntegration.obfuscatedApiKey]
  )
  useEffect(() => {
    if (AnrokIntegration.obfuscatedApiKey) {
      updateValidationState(true)
    }
  }, [AnrokIntegration.obfuscatedApiKey, updateValidationState])

  return (
    <Flex grow={1} width="100%">
      <Flex flexDirection="column" gap="20px" grow={1}>
        <AnrokAuthenticationCard
          onConnectionSuccess={updateCredentials}
          onCredentialsChange={resetValidationState}
          existingCredentials={existingCredentials}
        />
      </Flex>
    </Flex>
  )
}
